import { Box, Button, SxProps, Theme } from "@mui/material";
import {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import scrollHintTokens from "./ScrollHintRail.tokens";
import {
  railButtonSx,
  railChevronSx,
  railFadeSx,
  railScrollerSx,
  railWrapperSx,
} from "./ScrollHintRail.styles";

/** A press moves the rail by most of a screenful, keeping an item of context. */
const SCROLL_FRACTION = 0.8;
/**
 * Sub-pixel scroll positions mean `scrollLeft + clientWidth` never quite
 * reaches `scrollWidth`; anything inside this counts as "at the end".
 */
const END_EPSILON = 2;

const ChevronGlyph = ({ sx }: { sx?: SxProps<Theme> }) => (
  <Box
    component="svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2.4}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    sx={sx}
  >
    <path d="M9 6l6 6-6 6" />
  </Box>
);

/**
 * One entry of MUI's array `sx` form. `SxProps` itself admits an array, so it
 * cannot be the element type — an `SxProps[]` would allow the nested arrays
 * MUI rejects (TS2769).
 */
type SxEntry = Exclude<SxProps<Theme>, readonly any[]>;
const toSxArray = (sx?: SxProps<Theme>): SxEntry[] =>
  sx ? ((Array.isArray(sx) ? sx : [sx]) as SxEntry[]) : [];

export interface ScrollHintRailProps {
  children: ReactNode;
  /** Styles for the scrolling strip — the screen's own gap, padding, border. */
  railSx?: SxProps<Theme>;
  /** Styles for the wrapper, e.g. the margins the strip used to carry. */
  sx?: SxProps<Theme>;
  /** Forwarded to the scroller, so `role="tablist"` lands on the right node. */
  railProps?: Record<string, unknown>;
  /** Colour the fade resolves to; must match what the strip sits on. */
  surface?: string;
  /** Accessible names for the two buttons. */
  previousLabel?: string;
  nextLabel?: string;
}

/**
 * A horizontally scrolling strip that says so.
 *
 * The phone references draw these rails full-bleed with a hidden scrollbar, so
 * an item clipped by the viewport edge reads as a layout bug rather than an
 * invitation to swipe. This adds a fade and a chevron at whichever end still
 * has content beyond it.
 *
 * The chevrons are real buttons, not just gradients: a fade alone leaves the
 * action to a swipe, which is nothing to a keyboard or switch user.
 *
 * Both are absent from `sm` up, where these strips wrap instead of scrolling —
 * the state is driven by measurement, so nothing has to be told the breakpoint.
 */
const ScrollHintRail = ({
  children,
  railSx,
  sx,
  railProps,
  surface = scrollHintTokens.surface,
  previousLabel = "Scroll back",
  nextLabel = "Scroll forward",
}: ScrollHintRailProps) => {
  const railRef = useRef<HTMLDivElement | null>(null);
  // Tracked separately: a rail can have items off both ends at once, and each
  // chevron only earns its place while there is something that way.
  const [canScrollBack, setCanScrollBack] = useState(false);
  const [canScrollOn, setCanScrollOn] = useState(false);

  const sync = useCallback(() => {
    const rail = railRef.current;
    if (!rail) return;
    setCanScrollBack(rail.scrollLeft > END_EPSILON);
    setCanScrollOn(
      rail.scrollLeft + rail.clientWidth < rail.scrollWidth - END_EPSILON,
    );
  }, []);

  useEffect(() => {
    const rail = railRef.current;
    if (!rail) return;
    sync();
    rail.addEventListener("scroll", sync, { passive: true });
    /*
      A rail only overflows at some widths, and its content can change after
      mount (counts arriving, a tab set narrowing), so neither a one-off
      measurement nor a resize listener on the window is enough.
    */
    const observer = new ResizeObserver(sync);
    observer.observe(rail);
    Array.from(rail.children).forEach((child) => observer.observe(child));
    return () => {
      rail.removeEventListener("scroll", sync);
      observer.disconnect();
    };
  }, [sync, children]);

  const scrollBy = (direction: 1 | -1) => {
    const rail = railRef.current;
    if (!rail) return;
    rail.scrollBy({
      left: direction * rail.clientWidth * SCROLL_FRACTION,
      behavior: "smooth",
    });
  };

  return (
    <Box sx={[railWrapperSx, ...toSxArray(sx)]}>
      <Box
        ref={railRef}
        sx={[railScrollerSx, ...toSxArray(railSx)]}
        {...railProps}
      >
        {children}
      </Box>

      {canScrollBack && (
        <>
          <Box sx={railFadeSx("start", surface)} aria-hidden="true" />
          <Button
            disableRipple
            aria-label={previousLabel}
            onClick={() => scrollBy(-1)}
            sx={railButtonSx("start", surface)}
          >
            <ChevronGlyph sx={railChevronSx("start")} />
          </Button>
        </>
      )}

      {canScrollOn && (
        <>
          <Box sx={railFadeSx("end", surface)} aria-hidden="true" />
          <Button
            disableRipple
            aria-label={nextLabel}
            onClick={() => scrollBy(1)}
            sx={railButtonSx("end", surface)}
          >
            <ChevronGlyph sx={railChevronSx("end")} />
          </Button>
        </>
      )}
    </Box>
  );
};

export default ScrollHintRail;
